import { FC, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AiOutlineClose } from 'react-icons/ai';
import { Button, Flex, Icon, Stack, Text } from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import { useRequestApplicationDocs } from '~/core/shared/api/middle-office/application';
import {
  Dialog,
  FormField,
  Input,
  Select,
  Typeahead,
} from '~/core/shared/components';
import { EMPTY_ARR } from '~/core/shared/constants';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Checkbox } from '~/core/shared/lib/chakraUi/components/Checkbox';
import { DocsRequestPayload, FormFieldUpdateObject } from '~/core/shared/types';
import { handleError } from '~/core/shared/utils/admin';
import { DocsTabContext } from '../application/tabs';

type DocsRequestModalProps = {
  documentTypeCategory: string[];
};
export const DocsRequestModal: FC<DocsRequestModalProps> = ({
  documentTypeCategory,
}) => {
  const {
    uuid,
    definition,
    docsRequestModalApi,
    docTypes,
    documentOwnerOptions,
  } = useContext(DocsTabContext);
  const { requestDocs, isPending } = useRequestApplicationDocs(definition);
  const [requestedDocTypes, setRequestedDocTypes] =
    useState<string[]>(EMPTY_ARR);
  const [owner, setOwner] = useState<FormFieldUpdateObject<any>>();
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [documentRequirements, setDocumentRequirements] = useState<string>('');

  const form = useForm<DocsRequestPayload>({});

  useEffect(() => {
    form.setValue('documentRequirements', documentRequirements);
  }, [documentRequirements]);

  const addRequestedDocTypes = useEventHandler((type: string): void => {
    const selectedDocType = docTypes
      .flatMap((group) => group.options)
      .find((option) => option.value === type);

    if (selectedDocType?.meta?.documentRequirements) {
      setDocumentRequirements(
        selectedDocType.meta.documentRequirements as string,
      );
    } else {
      setDocumentRequirements(''); // Clear if no requirements are found
    }
    setRequestedDocTypes((types) => [...types, type]);
    form.setValue('type', null);
  });

  const removeRequestedDocTypes = useEventHandler((idx: number): void => {
    setRequestedDocTypes((types) => types.filter((type, i) => i !== idx));
  });

  const reset = useEventHandler((): void => {
    form.reset();
    setSendEmail(false);
    setRequestedDocTypes(EMPTY_ARR);
    setDocumentRequirements('');
  });

  const onSubmit = useEventHandler(
    async (formValues: {
      owner?: string;
      name?: string;
      documentRequirements?: string;
    }): Promise<void> => {
      if (requestedDocTypes.length === 0) {
        handleError('Please enter at least 1 document type');
        return;
      }
      const ownerDocumentName = formValues.name ?? owner?.value.label ?? '';

      const payload: DocsRequestPayload = {
        documentTypeCategory,
        sendEmail,
        types: requestedDocTypes,
        ...(owner ? { owner: owner.value.value } : {}),
        ...(requestedDocTypes.length > 0 &&
        requestedDocTypes.length <= 1 &&
        ownerDocumentName
          ? {
              name: ownerDocumentName,
            }
          : {}),
        ...(requestedDocTypes.length > 0 &&
        requestedDocTypes.length <= 1 &&
        formValues.documentRequirements
          ? {
              documentRequirements: formValues.documentRequirements,
            }
          : {}),
      };

      await requestDocs({ uuid, data: payload });
      reset();
      docsRequestModalApi.onClose();
      setOwner(undefined);
    },
  );

  const closeModal = useEventHandler((): void => {
    docsRequestModalApi.onClose();
    setDocumentRequirements('');
    setRequestedDocTypes(EMPTY_ARR);
  });

  const onOpenChange = useEventHandler(({ open }: UseOpenCloseReturn) => {
    if (open) return;
    closeModal();
  });

  return (
    <Dialog
      header="Request documents"
      onOpenChange={onOpenChange}
      open={docsRequestModalApi.open}
      size="lg"
      subHeader="Add additional document requirements to an application"
    >
      <FormProvider {...form}>
        <Stack as="form" gap={4} onSubmit={form.handleSubmit(onSubmit)}>
          {requestedDocTypes.length !== 0 && (
            <>
              <Text fontWeight="semibold">Additional document requests</Text>

              <Stack border="1px" gap={0}>
                {requestedDocTypes.map((requestedDocType, idx) => (
                  <Flex
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    alignItems="center"
                    bg="gray.50"
                    borderBottom={
                      idx !== requestedDocTypes.length - 1 ? '1px' : ''
                    }
                    justifyContent="space-between"
                    px={4}
                    py={2}
                  >
                    <Text>{requestedDocType}</Text>
                    <Icon
                      as={AiOutlineClose}
                      cursor="pointer"
                      onClick={() => removeRequestedDocTypes(idx)}
                    />
                  </Flex>
                ))}
              </Stack>
            </>
          )}

          <FormField
            borderColor="gray.300"
            component={Typeahead}
            groups={docTypes}
            hooks={{
              afterOnChange: (value) => {
                if (value) addRequestedDocTypes(value.value);
              },
            }}
            label="Document type request"
            name="type"
            required
            size="lg"
            variant="outline"
          />

          <FormField
            component={Select}
            groups={documentOwnerOptions}
            hooks={{ afterOnChange: (value) => setOwner(value) }}
            isClearable
            label="Document owner"
            name="owner"
            size="lg"
            variant="outline"
          />

          {requestedDocTypes.length <= 1 && (
            <>
              <FormField
                borderColor="gray.300"
                component={Input}
                label="Name"
                name="name"
                placeholder="Add optional name for the document"
                size="lg"
                variant="outline"
              />
              <FormField
                borderColor="gray.300"
                component={Input}
                defaultValue={documentRequirements}
                label="Document requirements"
                name="documentRequirements"
                size="lg"
                variant="outline"
              />
            </>
          )}

          <Flex>
            <Checkbox
              flexShrink={0}
              mr={4}
              onCheckedChange={(e) => setSendEmail(!!e.checked)}
              size="lg"
            />

            <Text>
              Send email notification of new requirements to introducer
            </Text>
          </Flex>

          <Stack direction="row" justifyContent="end">
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={closeModal}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              colorPalette="primary"
              disabled={isPending}
              fontSize="md"
              fontWeight="bold"
              type="submit"
              variant="solid"
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  );
};
